import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import {
  FiHeart, FiClock, FiShare2,
  FiBookmark, FiMessageCircle,
  FiArrowLeft, FiSend, FiMoreHorizontal,
  FiTrash2, FiEdit3
} from 'react-icons/fi';
import blogService from "../../services/blogService";
import favoritesService from "../../services/favoritesService";
import commentService from "../../services/commentService";
import { getUserId, getUserEmail, isAuthenticated } from "../../utils/authUtils";
import { normalizeBlogTypography } from "../../utils/blogTypography";
import BlogSearchBar from './BlogSearchBar';
import "./modern-blog-details.css";

const ADMIN_EMAILS = [
  process.env.REACT_APP_ADMIN,
  process.env.REACT_APP_ARI_ADMIN,
]
  .filter(Boolean)
  .map((email) => email.trim().toLowerCase());

const isConfiguredAdmin = (email) =>
  ADMIN_EMAILS.includes(String(email || "").trim().toLowerCase());

const ModernBlogDetails = (props) => {
  const { id } = useParams();
  const history = useHistory();
  const [blog, setBlog] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  
  // User interactions
  const [isLiked, setIsLiked] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  
  // Navigation state
  const [allBlogs, setAllBlogs] = useState([]);
  const [currentBlogIndex, setCurrentBlogIndex] = useState(-1);
  
  // Comment interactions
  const [commentLikes, setCommentLikes] = useState(new Map()); // commentId -> number of likes
  const [userCommentLikes, setUserCommentLikes] = useState(new Set()); // Set of commentIds user has liked
  const [showReplies, setShowReplies] = useState(new Map()); // commentId -> boolean
  const [replyingTo, setReplyingTo] = useState(null); // commentId user is replying to
  const [replyText, setReplyText] = useState(""); // Reply textarea content
  
  const { user } = props.user || {};
  const userId = getUserId(props.user);
  const userEmail = getUserEmail(props.user);
  const isUserAuthenticated = isAuthenticated(props.user);

  // Check if user is admin
  const isAdmin = useCallback(() => {
    return isConfiguredAdmin(userEmail || user?.email);
  }, [userEmail, user?.email]);

  // Debug logging
  useEffect(() => {
    console.log('ModernBlogDetails - User debug:', {
      propsUser: props.user,
      user: user,
      userId: userId,
      userEmail: userEmail,
      isUserAuthenticated: isUserAuthenticated
    });
  }, [props.user, user, userId, userEmail, isUserAuthenticated]);

  // Fetch all blogs for navigation
  const fetchAllBlogs = useCallback(async () => {
    try {
      const response = await blogService.getBlogs();
      const blogsData = Array.isArray(response) ? response : response?.data || [];
      
      const enhancedBlogs = await Promise.all(
        blogsData.map(async (blog) => await blogService.formatBlogData(blog))
      );
      setAllBlogs(enhancedBlogs);
      
      // Find current blog index - ensure both id types are strings for comparison
      const currentIndex = enhancedBlogs.findIndex(blog => String(blog.id) === String(id));
      setCurrentBlogIndex(currentIndex);
      
      console.log('Debug navigation:', {
        totalBlogs: enhancedBlogs.length,
        currentId: id,
        foundIndex: currentIndex,
        blogIds: enhancedBlogs.map(b => String(b.id)),
        canGoPrevious: currentIndex > 0,
        canGoNext: currentIndex < enhancedBlogs.length - 1
      });
    } catch (error) {
      console.error('Error fetching all blogs:', error);
    }
  }, [id]);

  // Get user display name
  const getUserDisplayName = useCallback(() => {
    // Prioritize firstName + lastName combination
    if (user?.firstName && user?.lastName) {
      const firstName = user.firstName.charAt(0).toUpperCase() + user.firstName.slice(1).toLowerCase();
      const lastName = user.lastName.charAt(0).toUpperCase() + user.lastName.slice(1).toLowerCase();
      return `${firstName} ${lastName}`;
    }
    
    // Use displayName if available (for Google/Facebook logins)
    if (user?.displayName) return user.displayName;
    if (user?.name) return user.name;
    
    // Use individual name fields
    if (user?.firstName) {
      return user.firstName.charAt(0).toUpperCase() + user.firstName.slice(1).toLowerCase();
    }
    if (user?.lastName) {
      return user.lastName.charAt(0).toUpperCase() + user.lastName.slice(1).toLowerCase();
    }
    
    // Fallback: extract from email for platform registrations
    if (user?.email) {
      const emailParts = user.email.split('@')[0];
      if (emailParts.includes('.')) {
        const nameParts = emailParts.split('.');
        const emailFirst = nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1);
        const emailLast = nameParts[1].charAt(0).toUpperCase() + nameParts[1].slice(1);
        return `${emailFirst} ${emailLast}`;
      } else {
        return emailParts.charAt(0).toUpperCase() + emailParts.slice(1);
      }
    }
    
    return 'Utilizator';
  }, [user]);

  // Navigation functions
  const goToPreviousBlog = useCallback(() => {
    if (currentBlogIndex < allBlogs.length - 1 && allBlogs.length > 0) {
      const nextBlog = allBlogs[currentBlogIndex + 1];
      history.push(`/blog-single-fullwidth/${nextBlog.id}`);
    }
  }, [currentBlogIndex, allBlogs, history]);

  const goToNextBlog = useCallback(() => {
    if (currentBlogIndex > 0 && allBlogs.length > 0) {
      const previousBlog = allBlogs[currentBlogIndex - 1];
      history.push(`/blog-single-fullwidth/${previousBlog.id}`);
    }
  }, [currentBlogIndex, allBlogs, history]);

  const fetchBlogData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await blogService.getBlogById(id);
      const blogData = await blogService.formatBlogData(response, true); // Include real comment count
      setBlog(blogData);

      // Update view count
      await blogService.updateBlogViews(id, response);
      setBlog(prev => prev ? { ...prev, views: (prev.views || 0) + 1 } : null);

      // Keep both the bookmark and the private like state visible for this user.
      if (userId) {
        try {
          const [favoritesResponse, likesResponse] = await Promise.all([
            favoritesService.getFavorites(userId, { type: 'blog', itemId: id }),
            favoritesService.getFavorites(userId, { type: 'blog-like', itemId: id })
          ]);
          setIsFavorited(favoritesResponse.data?.length > 0);
          setIsLiked(likesResponse.data?.length > 0);
        } catch (error) {
          console.error('Error checking saved blog interactions:', error);
        }
      }

    } catch (error) {
      console.error('Error fetching blog:', error);
      setError('Articolul nu a putut fi încărcat. Vă rugăm să încercați din nou.');
      toast.error('Eroare la încărcarea articolului');
    } finally {
      setLoading(false);
    }
  }, [id, userId]);

  // Fetch comments with likes and replies
  const fetchComments = useCallback(async () => {
    try {
      const commentsData = await commentService.getComments(id);
      const formattedComments = commentsData.map(comment => commentService.formatComment(comment));
      
      // Fetch likes and replies for each comment
      const commentsWithExtras = await Promise.all(
        formattedComments.map(async (comment) => {
          try {
            // Get likes for this comment
            const likesData = await commentService.getCommentLikes(comment.id, id, userId);
            
            // Get replies for this comment
            const repliesData = await commentService.getReplies(comment.id, id);
            
            return {
              ...comment,
              likes: likesData.data?.totalLikes || 0,
              userHasLiked: likesData.data?.userHasLiked || false,
              replies: repliesData.data || []
            };
          } catch (error) {
            console.error(`Error fetching extras for comment ${comment.id}:`, error);
            return {
              ...comment,
              likes: 0,
              userHasLiked: false,
              replies: []
            };
          }
        })
      );
      
      setComments(commentsWithExtras);
      
      // Update local state for likes
      const newCommentLikes = new Map();
      const newUserLikes = new Set();
      
      commentsWithExtras.forEach(comment => {
        newCommentLikes.set(comment.id, comment.likes);
        if (comment.userHasLiked) {
          newUserLikes.add(comment.id);
        }
      });
      
      setCommentLikes(newCommentLikes);
      setUserCommentLikes(newUserLikes);
      
    } catch (error) {
      console.error('Error fetching comments:', error);
      setComments([]);
    }
  }, [id, userId]);

  // Comment interaction functions
  const handleCommentLike = useCallback(async (commentId) => {
    if (!isUserAuthenticated || !userId) {
      toast.error('Trebuie să fiți autentificat pentru a aprecia comentarii');
      return;
    }

    try {
      const isLiked = userCommentLikes.has(commentId);
      const action = isLiked ? 'unlike' : 'like';
      
      // Update local state optimistically
      const newLikes = new Set(userCommentLikes);
      const newCommentLikes = new Map(commentLikes);
      
      if (isLiked) {
        newLikes.delete(commentId);
        newCommentLikes.set(commentId, Math.max(0, (commentLikes.get(commentId) || 0) - 1));
      } else {
        newLikes.add(commentId);
        newCommentLikes.set(commentId, (commentLikes.get(commentId) || 0) + 1);
      }
      
      setUserCommentLikes(newLikes);
      setCommentLikes(newCommentLikes);
      
      // Call backend
      await commentService.likeComment(commentId, id, userId, action);
      
      toast.success(isLiked ? 'Apreciere eliminată' : 'Comentariu apreciat!');
    } catch (error) {
      console.error('Error liking comment:', error);
      // Revert optimistic update
      const newLikes = new Set(userCommentLikes);
      const newCommentLikes = new Map(commentLikes);
      
      if (userCommentLikes.has(commentId)) {
        newLikes.delete(commentId);
        newCommentLikes.set(commentId, Math.max(0, (commentLikes.get(commentId) || 0) - 1));
      } else {
        newLikes.add(commentId);
        newCommentLikes.set(commentId, (commentLikes.get(commentId) || 0) + 1);
      }
      
      setUserCommentLikes(newLikes);
      setCommentLikes(newCommentLikes);
      toast.error('Eroare la aprecierea comentariului');
    }
  }, [userCommentLikes, commentLikes, isUserAuthenticated, userId, id]);

  const handleReply = useCallback((commentId) => {
    setReplyingTo(commentId);
    setReplyText("");
  }, []);

  const handleReplySubmit = useCallback(async (parentCommentId) => {
    if (!replyText.trim() || !isUserAuthenticated || !userId) {
      toast.error('Trebuie să fiți autentificat pentru a răspunde');
      return;
    }

    try {
      const replyData = {
        text: replyText.trim(),
        username: getUserDisplayName(),
        displayName: getUserDisplayName(),
        userId: userId,
        userEmail: userEmail,
        avatar: user?.photoURL || user?.picture || user?.profileImageUrl || user?.avatar || `/api/placeholder/40/40`
      };

      await commentService.postReply(id, parentCommentId, replyData);
      toast.success('Răspunsul a fost trimis!');
      setReplyText("");
      setReplyingTo(null);
      
      // Refresh comments to get the updated replies
      await fetchComments();
    } catch (error) {
      console.error('Error submitting reply:', error);
      toast.error('Eroare la trimiterea răspunsului');
    }
  }, [replyText, isUserAuthenticated, getUserDisplayName, userId, userEmail, user, id, fetchComments]);

  const toggleReplies = useCallback((commentId) => {
    const newShowReplies = new Map(showReplies);
    newShowReplies.set(commentId, !showReplies.get(commentId));
    setShowReplies(newShowReplies);
  }, [showReplies]);

  // Handle like toggle
  const handleLikeToggle = useCallback(async () => {
    if (!blog || !isUserAuthenticated || !userId) {
      toast.error('Trebuie să fiți autentificat pentru a aprecia articolele');
      return;
    }

    try {
      const newLikedState = !isLiked;
      setIsLiked(newLikedState);
      
      // Update local state optimistically
      setBlog(prev => ({
        ...prev,
        likes: newLikedState ? prev.likes + 1 : Math.max(0, prev.likes - 1)
      }));

      // Use the toggle functionality from favorites service
      const result = await favoritesService.toggleFavorite(userId, {
        itemId: blog.id,
        itemType: 'blog-like',
        title: blog.title,
        description: blog.excerpt || blog.description || '',
        category: blog.category || ''
      });

      if (result) {
        toast.success('Articol apreciat!');
      } else {
        toast.success('Apreciere eliminată');
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      // Revert optimistic update
      setIsLiked(!isLiked);
      setBlog(prev => ({
        ...prev,
        likes: isLiked ? prev.likes + 1 : Math.max(0, prev.likes - 1)
      }));
      toast.error('Eroare la actualizarea aprecierii');
    }
  }, [blog, userId, isUserAuthenticated, isLiked]);

  // Handle favorite toggle
  const handleFavoriteToggle = useCallback(async () => {
    if (!userId || !blog || !isUserAuthenticated) {
      toast.error('Trebuie să fiți autentificat pentru a adăuga la favorite');
      return;
    }

    try {
      const favoriteData = {
        userId: userId,
        itemId: blog.id,
        itemType: 'blog',
        title: blog.title,
        description: blog.excerpt,
        category: blog.category,
        imageUrl: blog.image || blog.coverImage,
        url: `/blog-single-fullwidth/${blog.id}`
      };

      if (isFavorited) {
        await favoritesService.removeFavorite(userId, blog.id, 'blog');
        setIsFavorited(false);
        toast.success('Articol eliminat din favorite');
      } else {
        await favoritesService.addFavorite(userId, favoriteData);
        setIsFavorited(true);
        toast.success('Articol adăugat la favorite');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Eroare la actualizarea favoritelor');
    }
  }, [userId, blog, isFavorited, isUserAuthenticated]);

  // Handle share
  const handleShare = useCallback(async () => {
    if (!blog) return;

    const shareUrl = window.location.href;
    const shareData = {
      title: blog.title,
      text: blog.excerpt,
      url: shareUrl
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copiat în clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copiat în clipboard!');
      } catch (clipboardError) {
        toast.error('Eroare la partajare');
      }
    }
  }, [blog]);

  // Handle delete blog
  const handleDeleteBlog = useCallback(async () => {
    if (!isAdmin()) {
      toast.error('Nu aveți permisiunea de a șterge articole');
      return;
    }

    const confirmDelete = window.confirm(
      `Sunteți sigur că doriți să ștergeți articolul "${blog.title}"?\n\nAceastă acțiune nu poate fi anulată.`
    );

    if (!confirmDelete) return;

    try {
      await blogService.deleteBlog(blog.id);
      toast.success('Articolul a fost șters cu succes!');
      // Redirect to blog list
      history.push('/blog');
    } catch (error) {
      console.error('Error deleting blog:', error);
      toast.error('Eroare la ștergerea articolului: ' + error.message);
    }
  }, [isAdmin, blog?.id, blog?.title, history]);

  // Handle update blog
  const handleUpdateBlog = useCallback(() => {
    if (!isAdmin()) {
      toast.error('Nu aveți permisiunea de a actualiza articole');
      return;
    }

    history.push(`/admin/blog/update/${blog.id}`);
  }, [isAdmin, blog?.id, history]);

  // Handle comment submission
  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !isUserAuthenticated || !userId) {
      toast.error('Trebuie să fiți autentificat pentru a comenta');
      return;
    }

    setSubmittingComment(true);
    try {
      const commentData = {
        text: newComment.trim(),
        username: getUserDisplayName(),
        displayName: getUserDisplayName(),
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        userId: userId,
        userEmail: userEmail,
        avatar: user?.photoURL || user?.picture || user?.profileImageUrl || user?.avatar || `/api/placeholder/40/40`
      };

      await commentService.postComment(id, commentData);
      toast.success('Comentariul a fost trimis!');
      setNewComment("");
      
      // Refresh comments
      await fetchComments();
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast.error('Eroare la trimiterea comentariului');
    } finally {
      setSubmittingComment(false);
    }
  };

  // Handle comment deletion
  const handleCommentDelete = useCallback(async (commentId, commentUserId, isReply = false, parentCommentId = null) => {
    const isAdmin = isConfiguredAdmin(userEmail);
    const isOwner = commentUserId === userId;
    
    if (!isAdmin && !isOwner) {
      toast.error('Nu aveți permisiunea să ștergeți acest comentariu');
      return;
    }

    const confirmMessage = isReply ? 'Sigur doriți să ștergeți acest răspuns?' : 'Sigur doriți să ștergeți acest comentariu?';
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      if (isReply && parentCommentId) {
        await commentService.deleteReply(commentId, parentCommentId, id);
        toast.success('Răspunsul a fost șters');
      } else {
        await commentService.deleteComment(commentId, id);
        toast.success('Comentariul a fost șters');
      }
      
      // Refresh comments
      await fetchComments();
    } catch (error) {
      console.error('Error deleting comment/reply:', error);
      toast.error(isReply ? 'Eroare la ștergerea răspunsului' : 'Eroare la ștergerea comentariului');
    }
  }, [userId, userEmail, fetchComments, id]);

  // Process blog content for display
  const processContent = (content) => {
    if (!content) return { main: '', highlighted: '' };

    const highlightStart = content.indexOf("/*");
    const highlightEnd = content.indexOf("*/");
    
    if (highlightStart > -1 && highlightEnd > -1 && highlightEnd > highlightStart) {
      const highlighted = content.substring(highlightStart + 2, highlightEnd);
      const before = content.substring(0, highlightStart);
      const after = content.substring(highlightEnd + 2);
      return {
        main: before + after,
        highlighted: highlighted
      };
    }
    
    return { main: content, highlighted: '' };
  };

  // Effects
  useEffect(() => {
    fetchBlogData();
    fetchComments();
    fetchAllBlogs();
  }, [fetchBlogData, fetchComments, fetchAllBlogs]);

  // Loading state
  if (loading) {
    return (
      <div className="modern-blog-details">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Se încarcă articolul...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !blog) {
    return (
      <div className="modern-blog-details">
        <div className="error-container">
          <h2>Articolul nu a fost găsit</h2>
          <p>{error || 'Articolul pe care îl căutați nu există sau a fost eliminat.'}</p>
          <Link to="/blog" className="back-button">
            <FiArrowLeft />
            Înapoi la Blog
          </Link>
        </div>
      </div>
    );
  }

  const { main, highlighted } = processContent(
    normalizeBlogTypography(blog.content || blog.text)
  );

  return (
    <div className="modern-blog-details">
      {/* Main Content */}
      <div className="main-content">
        {/* Container */}
        <div className="container">
          {/* Search Bar */}
          <BlogSearchBar currentBlogId={id} showLikeCounts={isAdmin()} />
          
          {/* Enhanced Navigation Section - Below Search Bar */}
          <div className="enhanced-navigation-section">
            <Link to="/blog" className="enhanced-back-btn">
              <FiArrowLeft />
              <span>Înapoi la Blog</span>
            </Link>
            
            {/* Blog Navigation Arrows */}
            <div className="blog-navigation-arrows">
              <button 
                className="nav-arrow prev-arrow"
                title="Articolul următor"
                disabled={currentBlogIndex >= allBlogs.length - 1 || allBlogs.length === 0}
                onClick={goToPreviousBlog}
              >
                <FiArrowLeft />
              </button>
              <button 
                className="nav-arrow next-arrow"
                title="Articolul anterior"
                disabled={currentBlogIndex <= 0 || allBlogs.length === 0}
                onClick={goToNextBlog}
              >
                <FiArrowLeft style={{ transform: 'rotate(180deg)' }} />
              </button>
            </div>
          </div>
          
        <article className="blog-article">
          {/* Article Header */}
          <header className="article-header">
            <div className="article-meta">
              <div className="reading-info">
                <span className="reading-time">
                  <FiClock />
                  {blog.readingTime} min citire
                </span>
              </div>
            </div>

            <h1 className="article-title">{blog.title}</h1>

          </header>

          {/* Featured Image */}
          {(blog.image || blog.coverImage) && (
            <div className="featured-image">
              <img
                src={blog.image || blog.coverImage}
                alt={blog.title}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Article Content */}
          <div className="article-content">
            {main && (
              <div 
                className="content-main"
                dangerouslySetInnerHTML={{ __html: main }}
              />
            )}

            {highlighted && (
              <blockquote className="highlighted-content">
                <div dangerouslySetInnerHTML={{ __html: highlighted }} />
              </blockquote>
            )}
          </div>

          {/* Article Footer */}
          <footer className="article-footer">
            <div className="tags-section">
              <h4>Tag-uri</h4>
              <div className="tags">
                {(blog.tags?.length ? blog.tags : [blog.subject || blog.category])
                  .filter(Boolean)
                  .map((tag) => (
                    <span className="tag" key={tag}>{tag}</span>
                  ))}
              </div>
            </div>

            <div className="engagement-section">
              <div className="engagement-buttons">
                <button
                  type="button"
                  onClick={handleLikeToggle}
                  className={`engagement-btn engagement-btn--icon ${isLiked ? 'active' : ''}`}
                  title={isLiked ? 'Elimină aprecierea' : 'Apreciază'}
                  aria-label={isLiked ? 'Elimină aprecierea' : 'Apreciază'}
                  aria-pressed={isLiked}
                >
                  <FiHeart aria-hidden="true" />
                </button>

                <button
                  type="button"
                  onClick={handleFavoriteToggle}
                  className={`engagement-btn engagement-btn--icon ${isFavorited ? 'active' : ''}`}
                  title={isFavorited ? 'Elimină din favorite' : 'Salvează'}
                  aria-label={isFavorited ? 'Elimină din favorite' : 'Salvează'}
                  aria-pressed={isFavorited}
                >
                  <FiBookmark aria-hidden="true" />
                </button>

                <button
                  type="button"
                  onClick={handleShare}
                  className="engagement-btn engagement-btn--icon"
                  title="Partajează"
                  aria-label="Partajează"
                >
                  <FiShare2 aria-hidden="true" />
                </button>
              </div>

              {isAdmin() && (
                <div className="admin-actions-section">
                    <button
                      type="button"
                      onClick={handleDeleteBlog}
                      className="admin-btn admin-btn-delete"
                      title="Șterge articolul"
                      aria-label="Șterge articolul"
                    >
                      <FiTrash2 aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={handleUpdateBlog}
                      className="main-page-text-edit__trigger article-edit-trigger"
                      title="Actualizează articolul"
                      aria-label="Actualizează articolul"
                    >
                      <FiEdit3 aria-hidden="true" />
                    </button>
                </div>
              )}
            </div>
          </footer>
        </article>

        {/* Comments Section */}
        <section className="comments-section" id="comments-section">
          <h3 className="comments-title">
            <FiMessageCircle />
            Comentarii ({comments.reduce((total, comment) => total + 1 + (comment.replies?.length || 0), 0)})
          </h3>

          {/* Comment Form */}
          {user?.email ? (
            <form onSubmit={handleCommentSubmit} className="comment-form">
              <div className="form-header">
                <div className="user-avatar">
                  {user.photoURL || user.picture ? (
                    <img src={user.photoURL || user.picture} alt="Avatar" />
                  ) : (
                    <span>{getUserDisplayName().charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <span className="username">
                  {getUserDisplayName()}
                </span>
              </div>
              
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Scrie comentariul tău aici..."
                className="comment-input"
                rows="4"
                required
              />
              
              <button 
                type="submit" 
                disabled={submittingComment || !newComment.trim()}
                className="submit-comment"
              >
                {submittingComment ? (
                  <>
                    <div className="mini-spinner"></div>
                    Se trimite...
                  </>
                ) : (
                  <>
                    <FiSend />
                    Trimite comentariul
                  </>
                )}
              </button>
            </form>
          ) : (
            <div className="login-prompt">
              <p>Trebuie să fiți autentificat pentru a comenta.</p>
              <button 
                className="login-button"
                onClick={() => history.push('/login')}
              >
                Autentificare
              </button>
            </div>
          )}

          {/* Comments List */}
          <div className="comments-list">
            {comments.length === 0 ? (
              <div className="no-comments">
                <FiMessageCircle />
                <p>Nu există comentarii încă. Fiți primul care comentează!</p>
              </div>
            ) : (
              comments.map((comment, index) => (
                <div key={comment.id || index} className="comment">
                  <div className="comment-header">
                    <div className="commenter-info">
                      <div className="commenter-avatar">
                        {comment.avatar && comment.avatar !== '/api/placeholder/40/40' ? (
                          <img src={comment.avatar} alt={comment.username || 'Utilizator anonim'} />
                        ) : (
                          <div className="avatar-placeholder">
                            <span>{(comment.username || 'U').charAt(0).toUpperCase()}</span>
                          </div>
                        )}
                      </div>
                      <div className="comment-meta">
                        <span className="commenter-name">
                          {comment.username || 'Utilizator anonim'}
                        </span>
                        <span className="comment-date">
                          {commentService.getRelativeTime(comment.createDate)}
                        </span>
                      </div>
                    </div>
                    <div className="comment-actions">
                      {(isUserAuthenticated && (comment.userId === userId || isConfiguredAdmin(userEmail))) && (
                        <button 
                          className="comment-delete-btn" 
                          title="Șterge comentariu"
                          onClick={() => handleCommentDelete(comment.id, comment.userId)}
                        >
                          <FiTrash2 />
                        </button>
                      )}
                      <button className="comment-options" title="Opțiuni comentariu">
                        <FiMoreHorizontal />
                      </button>
                    </div>
                  </div>
                  <div className="comment-content">
                    <p>{comment.text || comment.comment}</p>
                  </div>
                  
                  {/* Comment Interactions */}
                  <div className="comment-interactions">
                    <button 
                      className={`comment-like-btn ${userCommentLikes.has(comment.id) ? 'liked' : ''}`}
                      onClick={() => handleCommentLike(comment.id)}
                      title="Apreciază comentariul"
                    >
                      <FiHeart />
                      <span>{commentLikes.get(comment.id) || 0}</span>
                    </button>
                    
                    <button 
                      className="comment-reply-btn"
                      onClick={() => handleReply(comment.id)}
                      title="Răspunde la comentariu"
                    >
                      <FiMessageCircle />
                      <span>Răspunde</span>
                    </button>
                    
                    {/* Show replies toggle if there are replies */}
                    {comment.replies && comment.replies.length > 0 && (
                      <button 
                        className="toggle-replies-btn"
                        onClick={() => toggleReplies(comment.id)}
                        title="Afișează/ascunde răspunsuri"
                      >
                        {showReplies.get(comment.id) ? 'Ascunde' : 'Afișează'} răspunsuri ({comment.replies.length})
                      </button>
                    )}
                  </div>

                  {/* Reply Form */}
                  {replyingTo === comment.id && (
                    <div className="reply-form">
                      <div className="reply-header">
                        <div className="user-avatar">
                          {user.photoURL || user.picture ? (
                            <img src={user.photoURL || user.picture} alt="Avatar" />
                          ) : (
                            <span>{getUserDisplayName().charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <span className="username">{getUserDisplayName()}</span>
                      </div>
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder={`Răspunde la ${comment.username}...`}
                        className="reply-input"
                        rows="3"
                      />
                      <div className="reply-actions">
                        <button 
                          onClick={() => setReplyingTo(null)}
                          className="cancel-reply"
                        >
                          Anulează
                        </button>
                        <button 
                          onClick={() => handleReplySubmit(comment.id)}
                          disabled={!replyText.trim()}
                          className="submit-reply"
                        >
                          Răspunde
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Replies */}
                  {comment.replies && comment.replies.length > 0 && showReplies.get(comment.id) && (
                    <div className="comment-replies">
                      {comment.replies.map((reply, replyIndex) => (
                        <div key={reply.id || replyIndex} className="reply">
                          <div className="reply-header">
                            <div className="commenter-info">
                              <div className="commenter-avatar">
                                {reply.avatar && reply.avatar !== '/api/placeholder/40/40' ? (
                                  <img src={reply.avatar} alt={reply.username || 'Utilizator anonim'} />
                                ) : (
                                  <div className="avatar-placeholder">
                                    <span>{(reply.username || 'U').charAt(0).toUpperCase()}</span>
                                  </div>
                                )}
                              </div>
                              <div className="reply-meta">
                                <span className="replier-name">
                                  {reply.username || 'Utilizator anonim'}
                                </span>
                                <span className="reply-date">
                                  {commentService.getRelativeTime(reply.createDate)}
                                </span>
                              </div>
                            </div>
                            <div className="reply-actions">
                              {(isUserAuthenticated && (reply.userId === userId || isConfiguredAdmin(userEmail))) && (
                                <button 
                                  className="comment-delete-btn" 
                                  title="Șterge răspuns"
                                  onClick={() => handleCommentDelete(reply.id, reply.userId, true, comment.id)}
                                >
                                  <FiTrash2 />
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="reply-content">
                            <p>{reply.text || reply.comment}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </section>
        </div>
      </div>
      
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user
});

export default connect(mapStateToProps)(ModernBlogDetails);
